import { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { ChatAppApiClient, ChatContextItem } from '@trinity/chat';
import { DashboardConfigSchema } from '@trinity/chat/types/dashboard-config';
import type { CanonicalWidgetData } from '@trinity/chat/types/dashboard-data';
import { DashboardBuilderProvider } from '@trinity/chat-components/containers/Dashboard/DashboardBuilderProvider';
import { DashboardDataContainer } from '@trinity/chat-components/containers/Dashboard/DashboardDataContainer';
import { ConfigAwareDataProvider } from '@trinity/chat-components/containers/Dashboard/DataProvider';
import {
  ChatAPIProxyClient,
  CustomPageRendererContextProvider,
  type CustomPageDispatchAction,
} from '@trinity/custom-pages-registry';
import { BuiltInCodeProviderDataSchema, ThirdPartyProviderDataSchema } from '@trinity/management-app-client';
import { usePage } from '../../../hooks/usePage';
import { TableauEmbedding } from '../../TableauEmbedding/TableauEmbedding';
import { BuilderCanvas } from '../Builder/BuilderCanvas';
import { createDefaultRegistry } from '../registry';
import { CustomCodeRenderer } from './CustomCodeRenderer';

// ─── Styled ───────────────────────────────────────────────────────────────────

const ScrollArea = styled(Box)({
  flex: 1,
  overflow: 'auto',
  minHeight: 0,
});

const IframeRoot = styled(Box)({
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
});

const Centered = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  minHeight: 200,
});

const noopCustomPageDispatchAction: CustomPageDispatchAction = () => {};

function isValidWidgetItemPair(item: unknown, rawItem: unknown): boolean {
  return (
    !!item &&
    !!rawItem &&
    typeof item === 'object' &&
    typeof rawItem === 'object' &&
    (item as { type?: string }).type === 'widget' &&
    (rawItem as { type?: string }).type === 'widget' &&
    'widget' in item &&
    'widget' in rawItem &&
    !!(item as { widget?: unknown }).widget &&
    !!(rawItem as { widget?: unknown }).widget &&
    typeof (item as { widget?: unknown }).widget === 'object' &&
    typeof (rawItem as { widget?: unknown }).widget === 'object'
  );
}

function applySourceAttribution(item: unknown, rawItem: unknown): void {
  if (!isValidWidgetItemPair(item, rawItem)) return;
  const rawWidget = (rawItem as { widget?: Record<string, unknown> }).widget;
  if (rawWidget?.sourceAttribution && !(item as { widget: Record<string, unknown> }).widget.sourceAttribution) {
    (item as { widget: Record<string, unknown> }).widget.sourceAttribution = rawWidget.sourceAttribution;
  }
}

function preserveSourceAttribution(parsed: { blocks: Array<{ items?: unknown[] }> }, rawConfig: Record<string, unknown>) {
  if (!parsed) return;
  const rawBlocks = (Array.isArray(rawConfig.blocks) ? rawConfig.blocks : []) as Array<
    Record<string, unknown>
  >;
  for (let bi = 0; bi < parsed.blocks.length && bi < rawBlocks.length; bi++) {
    const parsedBlock = parsed.blocks[bi];
    if (!parsedBlock) continue;
    const rawBlock = rawBlocks[bi];
    const parsedItems = Array.isArray(parsedBlock.items) ? parsedBlock.items : [];
    const rawItems = (rawBlock && Array.isArray(rawBlock.items) ? rawBlock.items : []) as Array<
      Record<string, unknown>
    >;
    for (let ii = 0; ii < parsedItems.length && ii < rawItems.length; ii++) {
      applySourceAttribution(parsedItems[ii], rawItems[ii]);
    }
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

function renderThirdPartyPage(page: { name: string; providerData: unknown }): React.ReactElement {
  const providerResult = ThirdPartyProviderDataSchema.safeParse(page.providerData);
  if (!providerResult.success) {
    return (
      <Centered>
        <Typography variant="body2" color="text.secondary">
          No URL configured for this page.
        </Typography>
      </Centered>
    );
  }

  const provider = providerResult.data;

  if (provider.provider === 'figma') {
    return (
      <IframeRoot>
        <iframe
          src={provider.url}
          title={page.name}
          allowFullScreen
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
        />
      </IframeRoot>
    );
  }

  if (provider.provider === 'dmi') {
    const leftNavWidth = 62;
    const topNavHeightTotal = 208; // 60px top nav + 148px header
    const { topPadding, url } = provider;
    return (
      <IframeRoot
        style={{
          position: 'relative',
          width: `calc(100% + ${leftNavWidth}px)`,
          overflow: 'hidden',
          paddingTop: `${topPadding}px`,
        }}
      >
        <iframe
          src={url}
          title={page.name}
          style={{
            position: 'absolute',
            top: `calc(-${topNavHeightTotal}px + ${topPadding}px)`,
            left: `-${leftNavWidth}px`,
            width: '100%',
            height: `calc(100% + ${topNavHeightTotal}px - ${topPadding}px)`,
            border: 'none',
            boxSizing: 'border-box',
          }}
        />
      </IframeRoot>
    );
  }

  // tableau
  return (
    <IframeRoot>
      <TableauEmbedding displayUrl={provider.url} />
    </IframeRoot>
  );
}

type Props = {
  /** The ID of the page to render, as returned by chat-api /pages/:id. */
  pageId: number | null;
  /**
   * Optional function used to proxy agent data-source requests through the
   * host app's backend. Required for widgets with dataSource.type="agent".
   * If not provided, agent widgets will render empty.
   */
  proxyFetch?: (data: {
    agentId: number;
    extensionPath: string;
    brandIdentifier?: string;
  }) => Promise<CanonicalWidgetData | null>;
  /** Optional brand identifier — passed to the data provider so agent requests include brand context. */
  brandIdentifier?: string;
  /** Optional authenticated client used to initialize custom built-in page proxies client. */
  chatAuthenticatedClient?: ChatAppApiClient['authenticatedClient'] | null;
  /** Optional subject from the active chat context for custom built-in code pages. */
  chatContextSubject?: ChatContextItem['subject'] | null;
  /** Optional dispatcher for custom page actions. */
  dispatchCustomPageAction?: CustomPageDispatchAction;
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * PageRenderer — renders a Page (by ID) in view-only mode inside the chat app.
 *
 * Page types:
 *  - `dashboard` / `custom_dashboard`: renders the DashboardConfig using the
 *    BuilderCanvas in read-only mode (no toolbar, sidebar, or drag handles).
 *  - `third_party`: renders an external embed based on `providerData.provider`:
 *    - `figma`   — plain iframe with Figma-appropriate padding/height
 *    - `dmi`     — iframe with absolute-position cropping to hide the external navbar
 *    - `tableau` — Tableau JS API embed (`<tableau-viz>`)
 *  - `built_in_code`: renders one of the built-in code components based on
 *    `providerData.codePage`.
 */
export function PageRenderer({
  pageId,
  proxyFetch,
  brandIdentifier,
  chatAuthenticatedClient,
  chatContextSubject,
  dispatchCustomPageAction,
}: Props): React.ReactElement {
  const { data: page, isLoading, isError } = usePage(pageId);

  const registry = useMemo(() => createDefaultRegistry(), []);
  const dataProvider = useMemo(
    () => new ConfigAwareDataProvider(proxyFetch ?? (() => Promise.resolve(null)), brandIdentifier),
    [proxyFetch, brandIdentifier]
  );
  const customPageProxiesClient = useMemo(
    () => (chatAuthenticatedClient ? new ChatAPIProxyClient(chatAuthenticatedClient) : null),
    [chatAuthenticatedClient]
  );

  const initialConfig = useMemo(() => {
    if (page?.type !== 'dashboard' && page?.type !== 'custom_dashboard') return undefined;
    const rawConfig = page.currentVersion?.config;
    if (!rawConfig) return undefined;
    const result = DashboardConfigSchema.safeParse(rawConfig);
    if (!result.success) return undefined;
    // Preserve sourceAttribution on widgets — Zod may strip it if the server
    // saved with an older schema version that didn't include the field.
    const parsed = result.data;
    preserveSourceAttribution(parsed, rawConfig);
    return parsed;
  }, [page]);

  if (isLoading) {
    return (
      <Centered>
        <CircularProgress size={32} />
      </Centered>
    );
  }

  if (isError || !page) {
    return (
      <Centered>
        <Typography variant="body2" color="text.secondary">
          Unable to load page.
        </Typography>
      </Centered>
    );
  }

  if (page.type === 'third_party') {
    return renderThirdPartyPage(page);
  }

  if (page.type === 'built_in_code') {
    const providerResult = BuiltInCodeProviderDataSchema.safeParse(page.providerData);
    if (!providerResult.success) {
      return (
        <Centered>
          <Typography variant="body2" color="text.secondary">
            No code page configured for this page.
          </Typography>
        </Centered>
      );
    }

    return (
      <ScrollArea>
        <CustomPageRendererContextProvider
          value={{
            proxiesClient: customPageProxiesClient,
            subject: chatContextSubject ?? null,
            dispatchAction: dispatchCustomPageAction ?? noopCustomPageDispatchAction,
          }}
        >
          <CustomCodeRenderer codePage={providerResult.data.codePage} pageName={page.name} />
        </CustomPageRendererContextProvider>
      </ScrollArea>
    );
  }

  // dashboard/custom_dashboard with no valid config — show error rather than empty canvas
  if ((page.type === 'dashboard' || page.type === 'custom_dashboard') && !initialConfig) {
    return (
      <Centered>
        <Typography variant="body2" color="text.secondary">
          Unable to load dashboard configuration.
        </Typography>
      </Centered>
    );
  }

  // dashboard + custom_dashboard both use DashboardConfig rendering
  return (
    <ScrollArea>
      <DashboardBuilderProvider initialConfig={initialConfig} registry={registry}>
        <DashboardDataContainer provider={dataProvider} mode="live">
          <BuilderCanvas readOnly title={page.name} />
        </DashboardDataContainer>
      </DashboardBuilderProvider>
    </ScrollArea>
  );
}
